// client/src/components/plataforma/siteInstitucional/MensagensContatoPage.js
import React, { Fragment, useState, useCallback } from 'react';
import { Mail, Loader2, Trash2, Eye, ArrowLeft, Send, Phone, MoreVertical } from 'lucide-react';
import PageHeader from '../../layout/PageHeader';
import Pagination from '../../common/Pagination';
import ActionsDrawer from '../../common/ActionsDrawer';
import { useContactMessages, useMutateContactMessages } from '../../../hooks/useContactMessages';
import { useHomePersonalizadaGate, HomePersonalizadaUpsellBanner } from './useHomePersonalizadaGate';

const STATUS_LABELS = {
    new: 'Nova',
    read: 'Lida',
    replied: 'Respondida',
    archived: 'Arquivada',
};

const STATUS_COLORS = {
    new: { background: '#dbeafe', color: '#1e40af' },
    read: { background: '#f1f5f9', color: '#475569' },
    replied: { background: '#dcfce7', color: '#166534' },
    archived: { background: '#f1f5f9', color: '#64748b' },
};

const StatusBadge = ({ status }) => (
    <span className="status-badge" style={STATUS_COLORS[status] || STATUS_COLORS.read}>
        {STATUS_LABELS[status] || status}
    </span>
);

const MensagensContatoPage = () => {
    const { hasHomePersonalizada } = useHomePersonalizadaGate();
    const [currentPage, setCurrentPage] = useState(1);
    const [statusFiltro, setStatusFiltro] = useState('');
    const [mensagemSelecionada, setMensagemSelecionada] = useState(null);
    const [textoResposta, setTextoResposta] = useState('');
    const [mensagemAcoes, setMensagemAcoes] = useState(null);
    const itemsPerPage = 20;

    const { data, isLoading } = useContactMessages({
        page: currentPage,
        limit: itemsPerPage,
        ...(statusFiltro && { status: statusFiltro }),
    });
    const { updateStatus, remove, reply } = useMutateContactMessages();

    const mensagens = data?.data || [];
    const total = data?.totalCount || 0;

    const handleVer = useCallback((mensagem) => {
        setMensagemSelecionada(mensagem);
        setTextoResposta('');
        setMensagemAcoes(null);
        if (mensagem.status === 'new') {
            updateStatus.mutate({ id: mensagem._id, status: 'read' });
        }
    }, [updateStatus]);

    const handleVoltar = useCallback(() => {
        setMensagemSelecionada(null);
        setTextoResposta('');
    }, []);

    const handleExcluir = useCallback((id) => {
        if (!window.confirm('Tem certeza que deseja excluir esta mensagem?')) return;
        remove.mutate(id);
        if (mensagemSelecionada?._id === id) setMensagemSelecionada(null);
        setMensagemAcoes(null);
    }, [remove, mensagemSelecionada]);

    const handleEnviarResposta = useCallback(() => {
        if (!textoResposta.trim() || !mensagemSelecionada) return;
        reply.mutate({ id: mensagemSelecionada._id, replyMessage: textoResposta.trim() }, {
            onSuccess: () => {
                setMensagemSelecionada(prev => prev ? { ...prev, status: 'replied', replyMessage: textoResposta.trim() } : prev);
                setTextoResposta('');
            }
        });
    }, [textoResposta, mensagemSelecionada, reply]);

    return (
        <Fragment>
            <div className="page-management-container">
                <PageHeader
                    icon={<Mail />}
                    title="Mensagens de Contato"
                    subtitle={!isLoading ? `${total} mensage${total !== 1 ? 'ns' : 'm'}` : null}
                    actions={
                        !mensagemSelecionada && (
                            <select
                                className="form-input"
                                style={{ width: 'auto' }}
                                value={statusFiltro}
                                onChange={(e) => { setStatusFiltro(e.target.value); setCurrentPage(1); }}
                            >
                                <option value="">Todos os status</option>
                                <option value="new">Novas</option>
                                <option value="read">Lidas</option>
                                <option value="replied">Respondidas</option>
                                <option value="archived">Arquivadas</option>
                            </select>
                        )
                    }
                />

                {!hasHomePersonalizada && <HomePersonalizadaUpsellBanner />}

                {mensagemSelecionada && (
                    <div className="content-panel mt-6">
                        <div className="actions-bar" style={{ marginBottom: '1rem' }}>
                            <button onClick={handleVoltar} className="action-button action-button-secondary">
                                <ArrowLeft size={18} className="icon" /> Voltar
                            </button>
                        </div>

                        <div className="form-section">
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: '0.5rem' }}>
                                <div>
                                    <h4 className="form-section-title" style={{ marginBottom: 4 }}>{mensagemSelecionada.subject}</h4>
                                    <p style={{ margin: 0, color: 'var(--color-text-secondary)', fontSize: '0.875rem' }}>
                                        De: <strong>{mensagemSelecionada.name}</strong> — <a href={`mailto:${mensagemSelecionada.email}`}>{mensagemSelecionada.email}</a>
                                        {mensagemSelecionada.phone && (
                                            <> — <Phone size={12} className="icon" style={{ verticalAlign: 'middle' }} /> {mensagemSelecionada.phone}</>
                                        )}
                                    </p>
                                    <p style={{ margin: '4px 0 0', color: 'var(--color-text-secondary)', fontSize: '0.8rem' }}>
                                        {mensagemSelecionada.createdAt ? new Date(mensagemSelecionada.createdAt).toLocaleString('pt-BR') : ''}
                                    </p>
                                </div>
                                <StatusBadge status={mensagemSelecionada.status} />
                            </div>

                            <div style={{ marginTop: '1rem', padding: '1rem', background: 'var(--color-bg-subtle, #f8fafc)', borderRadius: 8, whiteSpace: 'pre-wrap' }}>
                                {mensagemSelecionada.message}
                            </div>

                            {mensagemSelecionada.replyMessage && (
                                <div style={{ marginTop: '1rem' }}>
                                    <label className="form-label">Sua resposta anterior</label>
                                    <div style={{ padding: '1rem', background: 'var(--color-bg-success-subtle, #ecfdf5)', borderRadius: 8, whiteSpace: 'pre-wrap' }}>
                                        {mensagemSelecionada.replyMessage}
                                    </div>
                                </div>
                            )}

                            <div style={{ marginTop: '1.5rem' }}>
                                <label className="form-label" htmlFor="resposta">Responder por e-mail</label>
                                <textarea
                                    id="resposta"
                                    className="form-input"
                                    rows="4"
                                    placeholder="Escreva sua resposta..."
                                    value={textoResposta}
                                    onChange={(e) => setTextoResposta(e.target.value)}
                                    disabled={!hasHomePersonalizada}
                                />
                            </div>
                        </div>

                        <div className="form-actions">
                            <button
                                onClick={() => handleExcluir(mensagemSelecionada._id)}
                                className="action-button action-button-danger"
                                disabled={remove.isPending || !hasHomePersonalizada}
                            >
                                <Trash2 size={18} className="icon" /> Excluir
                            </button>
                            <button
                                onClick={handleEnviarResposta}
                                className="action-button action-button-primary"
                                disabled={!textoResposta.trim() || reply.isPending || !hasHomePersonalizada}
                            >
                                {reply.isPending ? <Loader2 size={18} className="icon animate-spin" /> : <Send size={18} className="icon" />}
                                Enviar Resposta
                            </button>
                        </div>
                    </div>
                )}
            </div>

            {!mensagemSelecionada && (
                <div className="table-container">
                    <div className="table-wrapper">
                        {isLoading ? (
                            <div className="table-loading-state">
                                <Loader2 size={24} className="animate-spin" />
                                <span>Carregando mensagens...</span>
                            </div>
                        ) : mensagens.length > 0 ? (
                            <table className="styled-table">
                                <thead>
                                    <tr>
                                        <th style={{ width: 8 }}></th>
                                        <th style={{ textAlign: 'left' }}>Nome</th>
                                        <th style={{ textAlign: 'left' }}>Assunto</th>
                                        <th>Status</th>
                                        <th>Data</th>
                                        <th className="text-right">Ações</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {mensagens.map((m) => (
                                        <tr key={m._id} style={{ cursor: 'pointer', opacity: m.status === 'new' ? 1 : 0.75 }} onClick={() => handleVer(m)}>
                                            <td style={{ padding: '0 0 0 1rem' }}>
                                                {m.status === 'new' && (
                                                    <span style={{ display: 'inline-block', width: 8, height: 8, borderRadius: '50%', background: 'var(--color-primary)' }} />
                                                )}
                                            </td>
                                            <td style={{ textAlign: 'left', fontWeight: m.status === 'new' ? 600 : 400 }}>{m.name}</td>
                                            <td style={{ textAlign: 'left' }}>{m.subject}</td>
                                            <td><StatusBadge status={m.status} /></td>
                                            <td style={{ fontSize: '0.8rem', color: 'var(--color-text-secondary)' }}>
                                                {m.createdAt ? new Date(m.createdAt).toLocaleDateString('pt-BR') : '—'}
                                            </td>
                                            <td className="text-right">
                                                <button
                                                    onClick={(e) => { e.stopPropagation(); setMensagemAcoes(m); }}
                                                    className="action-icon-button"
                                                    title="Ações"
                                                >
                                                    <MoreVertical size={16} className="icon" />
                                                </button>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        ) : (
                            <div className="table-empty-state">
                                <Mail size={48} />
                                <h3>Nenhuma mensagem</h3>
                                <p>{statusFiltro ? 'Nenhuma mensagem com esse status.' : 'Ainda não há mensagens recebidas pelo formulário de contato.'}</p>
                            </div>
                        )}
                    </div>
                    <Pagination
                        totalItems={total}
                        itemsPerPage={itemsPerPage}
                        currentPage={currentPage}
                        onPageChange={setCurrentPage}
                    />
                </div>
            )}

            <ActionsDrawer
                open={!!mensagemAcoes}
                onClose={() => setMensagemAcoes(null)}
                title={mensagemAcoes?.subject || 'Mensagem'}
                subtitle={mensagemAcoes?.name}
                actions={mensagemAcoes ? [
                    {
                        key: 'ver',
                        label: 'Ver Mensagem',
                        icon: <Eye size={20} className="icon" />,
                        onClick: () => handleVer(mensagemAcoes),
                    },
                    {
                        key: 'excluir',
                        label: 'Excluir',
                        icon: <Trash2 size={20} className="icon" />,
                        onClick: () => handleExcluir(mensagemAcoes._id),
                        destructive: true,
                        disabled: !hasHomePersonalizada,
                    },
                ] : []}
            />
        </Fragment>
    );
};

export default MensagensContatoPage;
